import { useEffect, useRef, useState } from "react";
import { JoinData } from './join_pages/JoinDataInt';
import SidebarItem from './SidebarItem';
import BottomSignalText from './components/BottomSignalText';
import pageConfigs, { PageConfig } from './data/pageConfigData';
import './App.scss';

interface TooltipState {
    visible: boolean;
    text: { text: string, bold?: boolean }[];
    x: number;
    y: number;
    width: number;
    height: number;
    class: string;
}

type PageName = keyof typeof pageConfigs;

const titleCase = (str: string) => {
    if (!str) return "";
    const splitStr = str.toLowerCase().split(" ");
    for (let i = 0; i < splitStr.length; i++) {
        if (splitStr[i]) {
            splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
        }
    }
    // Directly return the joined string
    return splitStr.join(" ");
};

const urlParams = new URLSearchParams(window.location.search);
const urlPage = urlParams.get('page');
const urlSlideOut = urlParams.get('slideout');

// It really doesn't matter if these are exposed, it's just a simple password
const password = 'tahoe2025';
const cookieHash = 'ad8a2379addbddc0800a3b8d83018e53';

const cookiesOnLoad = document.cookie;

const App = () => {
    const svgRef = useRef<SVGSVGElement>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<SVGTextElement>(null);
    const tooltipTimeoutRef = useRef<number | null>(null);

    const [isPasswordValid, setIsPasswordValid] = useState<boolean>(cookiesOnLoad.split('=')[1] === cookieHash);
    const [showRightSidebar, setShowRightSidebar] = useState(false);
    const [currentPageData, setCurrentPageData] = useState<PageConfig>(pageConfigs.homepage);
    const [currentPageImage, setCurrentPageImage] = useState<string>(pageConfigs.homepage.image);
    const [currentPage, setCurrentPage] = useState<string>('Homepage');
    const [newURLPage, setNewURLPage] = useState<string | null>(null);

    useEffect(() => {
        let addressBar = '';
        if (urlPage) {
            addressBar = '?page=' + urlPage;
            setNewURLPage(urlPage);
            changePage(urlPage);
        } else {
            addressBar = '?page=homepage';
            setNewURLPage('homepage');
        }

        if (urlSlideOut) {
            addressBar += '&slideout=' + urlSlideOut;
            if (urlSlideOut === 'opened') {
                setShowRightSidebar(true);
            } else {
                setShowRightSidebar(false);
            }
        } else {
            addressBar += '&slideout=closed';
        }

        window.history.replaceState({}, '', addressBar);
    }, []);

    const updateURL = (nextPage: string, nextSlideOut: string) => {
        let addressBar = '?page=' + nextPage;
        addressBar += '&slideout=' + nextSlideOut;

        window.history.replaceState({}, '', addressBar);
    }

    const updateSidebar = () => {
        setShowRightSidebar(prev => {
            if (prev === true) {
                updateURL(newURLPage ?? 'homepage', 'closed');
            } else {
                updateURL(newURLPage ?? 'homepage', 'opened');
            }
            return !prev;
        });
    }

    // window.history.replaceState(nextState, nextTitle, nextURL);

    const [tooltip, setTooltip] = useState<TooltipState>({
        visible: false,
        text: [],
        x: 0,
        y: 0,
        width: 80,
        height: 55,
        class: ''
    });

    const highlightRectsAndDivs = (
        dataJoinValue: string | null,
        action: "add" | "remove"
    ) => {
        if (!dataJoinValue) return;

        // Highlight corresponding rects
        if (svgRef.current) {
            const rects = svgRef.current.querySelectorAll(
                `rect[data-join="${dataJoinValue}"]`
            );
            rects.forEach((rect) => {
                rect.classList[action]("hover-box");
            });
        }

        // Highlight corresponding divs
        if (sidebarRef.current) {
            // const trs = sidebarRef.current.querySelectorAll(
            //     `tr[data-join="${dataJoinValue}"]`
            // );
            // trs.forEach((tr) => {
            //     tr.classList[action]("highlight-div");
            // });
            const divs = sidebarRef.current.querySelectorAll(
                `div[data-join="${dataJoinValue}"]`
            );

            divs.forEach((div) => {
                div.classList[action]("highlight-div");
            });
        }
    };

    const handleDivHover = (
        e: React.MouseEvent<HTMLDivElement>,
        onOrOff: "on" | "off"
    ) => {
        const dataJoinValue = e.currentTarget.getAttribute("data-join");
        highlightRectsAndDivs(dataJoinValue, onOrOff === "on" ? "add" : "remove");
    };

    const handleRectHover = (
        e: React.MouseEvent<SVGRectElement>,
        onOrOff: "on" | "off"
    ) => {
        const dataJoinValue = e.currentTarget.getAttribute("data-join");
        highlightRectsAndDivs(dataJoinValue, onOrOff === "on" ? "add" : "remove");
    };

    const extractTextFromSVG = (svgTextElement: SVGTextElement) => {
        // Get all <tspan> elements within the <text> element
        const tspans = svgTextElement.querySelectorAll("tspan");

        // Extract the text content from each <tspan>
        const extractedTexts = Array.from(tspans).map(tspan => tspan.textContent);

        return extractedTexts;
    }

    useEffect(() => {
        if (textRef.current && tooltip.visible) {
            // Force a layout recalculation
            textRef.current.getBoundingClientRect();
            const textWidth = textRef.current.getComputedTextLength();
            const tspanCount = textRef.current.querySelectorAll('tspan').length;
            let newHeight = tooltip.height;

            // console.log('tspanCount', tspanCount);
            if (tspanCount === 3) {
                newHeight = 26 * (tspanCount - 1);
            } else if (tspanCount === 4) {
                newHeight = 30 * (tspanCount - 1);
            } else if (tspanCount === 6) {
                newHeight = 26 * (tspanCount - 1);
            } else if (tspanCount === 8) {
                newHeight = 26 * (tspanCount - 1);
            } else if (tspanCount === 10) {
                newHeight = 24 * (tspanCount - 1);
            } else if (tspanCount > 3) {
                newHeight = 30 * (tspanCount - 1);
            }

            const nameAndSignal = extractTextFromSVG(textRef.current);
            const name = nameAndSignal[0];
            const signal = nameAndSignal[1];

            let newWidth = Math.ceil(textWidth) + 20;
            if (name?.length && signal?.length) {
                let longestText = name.length > signal.length ? name.length : signal.length;
                nameAndSignal.forEach((text) => {
                    if (text && text.length > longestText) {
                        longestText = text.length;
                    }
                });
                newWidth = Number(longestText) * 8;
            }

            if (newWidth !== tooltip.width) {
                setTooltip(prev => ({ ...prev, width: newWidth, height: newHeight }));
            } else if (newHeight !== tooltip.height) {
                setTooltip(prev => ({ ...prev, height: newHeight }));
            }
        }
    }, [tooltip.height, tooltip.text, tooltip.visible, tooltip.width]);

    const showTooltip = (e: React.MouseEvent<SVGRectElement>, data: JoinData): void => {
        if (tooltipTimeoutRef.current) {
            clearTimeout(tooltipTimeoutRef.current);
        }

        const svg = svgRef.current;
        if (!svg) return;

        const CTM = svg.getScreenCTM();
        if (!CTM) return;

        // Add small offsets to prevent the tooltip from interfering with mouse events
        const x = (e.clientX - CTM.e + 10) / CTM.a; // Increased offset
        const y = (e.clientY - CTM.f + 20) / CTM.d; // Increased offset

        const joinData = data.join.split('-');
        const dataType = titleCase(joinData[0]);
        const signalType = titleCase(joinData[1]);
        const combinedTexts: { text: string, bold?: boolean }[] = [{ text: data.name, bold: true }];
        let signalText = '';

        if (dataType && dataType !== '') {
            signalText = dataType;
        }

        if (signalType && signalType !== '') {
            signalText += ' ' + signalType;
        }

        if (joinData[2] && joinData[2] !== '') {
            signalText += ' ' + joinData[2];
        } else {
            // Just for filler, shouldn't come up when signals are set properly
            signalText += ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ';
        }

        combinedTexts.push({ text: signalText, bold: false });

        const buildOutput = (data: JoinData, combinedTexts: { text: string, bold?: boolean }[]) => {
            const joinData = data.join.split('-');
            const dataType = titleCase(joinData[0]);
            const signalType = titleCase(joinData[1]);
            let signalText = '';

            combinedTexts.push({ text: data.name, bold: true });

            if (dataType && dataType !== '') {
                signalText = dataType;
            }

            if (signalType && signalType !== '') {
                signalText += ' ' + signalType;
            }

            if (joinData[2] && joinData[2] !== '') {
                signalText += ' ' + joinData[2];
            } else {
                // Just for filler, shouldn't come up when signals are set properly
                signalText += ' ' + ' ' + ' ' + ' ' + ' ' + ' ' + ' ';
            }

            combinedTexts.push({ text: signalText });
        }

        // Do group stuff here
        if (data.group !== undefined) {
            const groupedData = currentPageData.data.filter((roomInfo) => roomInfo.group === data.group && roomInfo.join !== data.join);
            if (groupedData.length > 0) {
                groupedData.forEach((groupData) => {
                    buildOutput(groupData, combinedTexts);
                });
            }
        }

        setTooltip({
            visible: true,
            text: combinedTexts,
            x,
            y,
            width: 80,
            height: 55,
            class: joinData[0],
        });
    };

    const hideTooltip = (): void => {
        // Add a small delay before hiding the tooltip
        tooltipTimeoutRef.current = setTimeout(() => {
            setTooltip(prev => ({ ...prev, visible: false }));
        }, 75); // 50ms delay
    };

    useEffect(() => {
        // Cleanup timeout on component unmount
        return () => {
            if (tooltipTimeoutRef.current) {
                clearTimeout(tooltipTimeoutRef.current);
            }
        };
    }, []);

    const changePage = (name: PageName) => {
        if (!pageConfigs[name]) return;

        setCurrentPage(name.toString());
        setCurrentPageImage(pageConfigs[name].image);

        if (pageConfigs[name]) {
            setCurrentPageData(pageConfigs[name]);
        }
        updateURL(name.toString(), urlSlideOut ?? 'closed');
    };

    // https://www.image-map.net/ - helpful but not what we're using atm
    return (
        <>
            {!isPasswordValid && (
                <div className='password-container'>
                    <input
                        type='password'
                        placeholder='Enter Password'
                        onChange={(e) => {
                            if (e.target.value === password) {
                                setIsPasswordValid(true);
                                document.cookie = 'password=' + cookieHash;
                            }
                        }}
                    />
                </div>
            )}

            {isPasswordValid === true && <div className="App" data-page-name={currentPage}>
                <div className='left-sidebar'>
                    <h3>Tahoe Documentation &nbsp; Pages</h3>
                    <div className='page-buttons'>
                        {Object.keys(pageConfigs).map((page, index) => {
                            const pageName = page.replace(/-/g, ' ');
                            return (
                                <button
                                    key={index}
                                    className={`${currentPage === pageName ? 'active' : ''}`}
                                    onClick={() => {
                                        changePage(page);
                                    }}
                                >
                                    {pageName.split(/(?=[A-Z])/).join(' ')}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {currentPage !== 'allSignals' && <div className="image-container">
                    {/* https://editor.method.ac/ - make sure to set the document height and width to 1280x800 before drawing */}
                    <svg
                        ref={svgRef}
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                        viewBox="0 0 1280 800"
                        overflow={"visible"}
                    >
                        <image
                            width="1280"
                            height="800"
                            xlinkHref={currentPageImage}
                        ></image>
                        {currentPageData.data.map((roomInfo) => {
                            return (
                                <rect
                                    key={roomInfo.join + roomInfo.name + (roomInfo?.group ?? '') + roomInfo.x + roomInfo.y + roomInfo?.category}
                                    className='hover-rect'
                                    data-join={roomInfo.join}
                                    data-hint={roomInfo.name}
                                    x={roomInfo.x}
                                    y={roomInfo.y}
                                    fill="transparent"
                                    opacity="0"
                                    width={roomInfo.width}
                                    height={roomInfo.height}
                                    stroke={roomInfo?.colour ?? '#FFF'}
                                    onMouseEnter={(e) => {
                                        showTooltip(e, roomInfo);
                                        handleRectHover(e, "on");
                                    }}
                                    onMouseOut={(e) => {
                                        hideTooltip();
                                        handleRectHover(e, "off");
                                    }}
                                />
                            );
                        })}

                        {/* https://www.petercollingridge.co.uk/tutorials/svg/interactive/tooltip/ then converted to React in Claude.AI */}
                        <g
                            className={`tooltip ${tooltip.class}`}
                            transform={`translate(${tooltip.x} ${tooltip.y})`}
                            // visibility={tooltip.visible ? "visible" : "hidden"}
                            style={{
                                opacity: tooltip.visible ? 1 : 0,
                                transition: 'opacity 0.15s ease-in-out',
                                pointerEvents: 'none', // Prevent tooltip from interfering with mouse events
                                height: tooltip.height,
                                width: tooltip.width
                            }}
                        >
                            <rect
                                width={tooltip.width}
                                height={tooltip.height}
                                fill="white" rx={2} ry={2} />
                            <text
                                ref={textRef}
                                x={12}
                                y={16}
                                dominantBaseline="middle"
                            >
                                {tooltip.text.map((line, index) => (
                                    <tspan
                                        key={index}
                                        x="12"
                                        dy={index === 0 ? "0" : "20"}
                                        fontWeight={line.bold ? 'bold' : 'normal'}
                                    >
                                        {line.text}
                                    </tspan>
                                ))}
                            </text>
                        </g>
                    </svg>

                    <div className='page-signal-container'>
                        {currentPageData?.pageSignal && <BottomSignalText pageSignal={currentPageData.pageSignal} />}
                    </div>
                </div>}

                {currentPage === 'allSignals' && <div>
                    {currentPageData.data.length}
                </div>}

                <div className={`sidebar-legend ${showRightSidebar ? 'opened' : 'closed'}`} ref={sidebarRef}>
                    <div
                        className='sidebar-toggle'
                        style={{
                            right: showRightSidebar ? '18.4%' : '0', // Adjust toggle position dynamically
                        }}
                        onClick={() => updateSidebar()}
                    >
                        {showRightSidebar
                            ? <svg fill="#000000" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" enableBackground="new 0 0 100 100" xmlSpace="preserve" transform="matrix(-1, 0, 0, 1, 0, 0)"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M44.942,50.412l14.037-15.487c0.742-0.818,0.68-2.083-0.139-2.824c-0.817-0.742-2.083-0.679-2.824,0.139L40.784,49.044 c-0.409,0.451-0.565,1.038-0.493,1.598c-0.016,0.564,0.196,1.131,0.647,1.539L57.74,67.412c0.383,0.348,0.864,0.519,1.344,0.519 c0.545,0,1.087-0.222,1.482-0.657c0.741-0.818,0.68-2.083-0.139-2.824L44.942,50.412z"></path> <path d="M84.133,49.756c0-18.448-15.01-33.457-33.458-33.457S17.218,31.308,17.218,49.756c0,18.449,15.009,33.458,33.457,33.458 S84.133,68.205,84.133,49.756z M50.675,79.214c-16.243,0-29.457-13.215-29.457-29.458c0-16.242,13.214-29.457,29.457-29.457 c16.243,0,29.458,13.215,29.458,29.457C80.133,65.999,66.918,79.214,50.675,79.214z"></path> </g> </g></svg>
                            : <svg fill="#000000" version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 0 100 100" enableBackground="new 0 0 100 100" xmlSpace="preserve"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M44.942,50.412l14.037-15.487c0.742-0.818,0.68-2.083-0.139-2.824c-0.817-0.742-2.083-0.679-2.824,0.139L40.784,49.044 c-0.409,0.451-0.565,1.038-0.493,1.598c-0.016,0.564,0.196,1.131,0.647,1.539L57.74,67.412c0.383,0.348,0.864,0.519,1.344,0.519 c0.545,0,1.087-0.222,1.482-0.657c0.741-0.818,0.68-2.083-0.139-2.824L44.942,50.412z"></path> <path d="M84.133,49.756c0-18.448-15.01-33.457-33.458-33.457S17.218,31.308,17.218,49.756c0,18.449,15.009,33.458,33.457,33.458 S84.133,68.205,84.133,49.756z M50.675,79.214c-16.243,0-29.457-13.215-29.457-29.458c0-16.242,13.214-29.457,29.457-29.457 c16.243,0,29.458,13.215,29.458,29.457C80.133,65.999,66.918,79.214,50.675,79.214z"></path> </g> </g></svg>
                        }
                    </div>

                    <div className={`sidebar-content ${showRightSidebar ? 'opened' : 'closed'}`}>
                        {currentPageData.data.map((roomInfo) => {
                            const joinInfo = roomInfo.join.split("-");
                            return <SidebarItem
                                key={roomInfo.join + '-' + roomInfo.name + '-' + (roomInfo?.group ?? '') + roomInfo.x + roomInfo.y + roomInfo?.category}
                                joinSignal={roomInfo.join}
                                joinSignalLong={joinInfo[2]}
                                mouseEnter={(e) => handleDivHover(e, "on")}
                                mouseLeave={(e) => handleDivHover(e, "off")}
                                name={roomInfo.name}
                                dataType={titleCase(joinInfo[0])}
                                eventType={titleCase(joinInfo[1])}
                            />
                        })}
                    </div>
                </div>
            </div>}
        </>
    )
}

export default App
